import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, DollarSign, Shield, Settings, LandPlot, Users, FileCheck } from 'lucide-react';

const services = [
  {
    icon: TrendingUp,
    title: 'Revenue System Development',
    description: 'Build revenue ecosystems that maximize burial volume, optimize pricing, and create diversified income streams.',
  },
  {
    icon: DollarSign,
    title: 'Pre-Need Sales Architecture',
    description: 'Design pre-need programs generating predictable revenue years ahead. Develop agent networks and commission structures.',
  },
  {
    icon: Shield,
    title: 'Insurance Distribution Strategy',
    description: 'Navigate insurance-backed preneed contracts with compliant structures that protect families while generating revenue.',
  },
  {
    icon: Settings,
    title: 'Operational Modernization',
    description: 'Modernization focused on operational reliability, record integrity, and sustainable margin improvement.',
  },
  {
    icon: Settings,
    title: 'Technology & Automation',
    description: 'Implement technology solutions that reduce costs, improve accuracy, and enhance the family experience.',
  },
  {
    icon: FileCheck,
    title: 'Regulatory & Audit Oversight',
    description: 'Ensure compliance with state regulations, trust requirements, and financial reporting standards.',
  },
  {
    icon: LandPlot,
    title: 'Land Expansion & Entitlement',
    description: 'Identify opportunities for cemetery expansion. Navigate zoning to add long-term asset value.',
  },
  {
    icon: Users,
    title: 'Board & Governance Alignment',
    description: 'Work with volunteer boards to establish clear strategic direction and accountability frameworks.',
  },
];

export default function Services() {
  return (
    <div>
      {/* Hero */}
      <section className="bg-slate-900 text-white py-12 md:py-16">
        <div className="container-custom">
          <div className="max-w-3xl">
            <h2 className="font-serif text-3xl md:text-4xl font-medium mb-4">
              Services
            </h2>
            <p className="text-lg text-gray-300">
              Operational and strategic services designed to stabilize, scale, and 
              modernize cemetery and deathcare organizations.
            </p>
          </div>
        </div>
      </section>

      {/* Services Prose */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            
            <h2 className="font-serif text-2xl md:text-3xl font-medium text-slate-900 mb-10">
              What Black Swan Cemetery Consulting Does For You
            </h2>

            <div className="space-y-10">
              
              <div>
                <h3 className="font-serif text-xl font-medium text-slate-900 mb-3">
                  Operations, Management, and Performance
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-3">
                  Black Swan Cemetery Consulting was built for cemetery owners who are ready to close the gap between where their operation is today and where it needs to be. As your fractional COO cemetery partner, Todd Sexton delivers cemetery operations consulting that cuts through the noise and addresses the systems, staffing, and workflow problems that are costing you time and money every single day. When you bring in a cemetery consultant from Black Swan, you are not getting a report and a handshake — you are getting hands-on cemetery COO services that stay with you until the work is done. Cemetery management consulting at this level means someone is in your corner who has seen these problems before and knows exactly how to fix them.
                </p>
              </div>

              <div>
                <h3 className="font-serif text-xl font-medium text-slate-900 mb-3">
                  Revenue and Financial Clarity
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-3">
                  Most cemetery owners have no clear picture of where their revenue is coming from, where it is going, or how much more their business could generate with the right strategy in place. Black Swan Cemetery Consulting provides direct cemetery revenue consulting that examines your pricing, your pre-need program, your sales process, and your merchandise mix to uncover the fastest and most reliable paths to stronger income. Detailed cemetery financial analysis then gives you the numbers behind the strategy — real data on your margins, your costs, and your true business value. This is the foundation of serious cemetery business consulting, and it is where most engagements with Black Swan begin.
                </p>
              </div>

              <div>
                <h3 className="font-serif text-xl font-medium text-slate-900 mb-3">
                  Planning and Long-Term Growth Strategy
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-3">
                  A cemetery that is not planning for its physical and financial future is actively losing ground to competitors who are. Black Swan Cemetery Consulting brings structured cemetery master planning to help you think beyond this quarter and build a property and revenue strategy that compounds over time. Through focused cemetery business consulting, Todd Sexton works with you to map out expansion decisions, capital investments, and operational improvements in the right sequence so that every dollar you spend moves you closer to a stronger, more valuable business. Whether you are three years into ownership or thirty, the right cemetery management consulting framework changes everything about how your business performs.
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Engagement Models */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <h2 className="section-heading">Engagement Models</h2>
            <p className="section-subheading mx-auto mt-3">
              All engagements begin with clear scope, defined deliverables, and agreed reporting structure.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-content mx-auto">
            {[
              { title: 'Fractional COO', desc: '6-18 months hands-on leadership' },
              { title: 'Revenue Diagnostic', desc: '90-day comprehensive assessment' },
              { title: 'Exit Advisory', desc: 'Position for sale or succession' },
              { title: 'Retainer', desc: 'Ongoing strategic guidance' },
            ].map((model, index) => (
              <div key={index} className="bg-white p-4 border border-gray-100">
                <h3 className="font-serif text-base font-medium text-slate-900 mb-1">
                  {model.title}
                </h3>
                <p className="text-slate-600 text-xs">
                  {model.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-slate-900 text-white">
        <div className="container-custom text-center">
          <h2 className="font-serif text-2xl font-medium mb-4">
            Discuss Your Organization's Needs
          </h2>
          <p className="text-gray-400 max-w-xl mx-auto mb-8">
            Every organization faces unique challenges. Let's discuss how targeted 
            executive leadership can drive measurable results.
          </p>
          <Link to="/contact" className="btn-primary">
            Schedule a Confidential Consultation
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
